import React, { useCallback, useEffect, useState } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import {
  AlignItems,
  Body2,
  Box,
  ButtonColorVariant,
  ButtonSize,
  Color,
  Display,
  Heading3,
  Heading4,
  JustifyContent,
  Modal,
  ModalBody,
  ModalButton,
  ModalClose,
  ModalFooter,
  ModalImageHeader,
  Space,
  Link,
  withCitrusTheme,
  useModal,
  Icon,
  FlexDirection,
  AlignSelf,
} from '@gousto-internal/citrus-react'
import Loading from 'Loading'

import config from 'config/routes'
import { trackSubscriptionSettingsChange } from 'routes/Account/Subscription/tracking'

import css from './SubPauseOSRView.css'

const LoadingWithPadding = withCitrusTheme(Loading, (theme) => ({
  padding: theme.spacing(10),
}))

interface Props {
  promoCode?: string
  offers?: string[]
  onConfirm: () => void
  onKeep: () => void
  title: string
  triggered: boolean
  modalType: string
  getPauseRecoveryContent: () => void
  modalVisibilityChange: (payload: { modalVisibility: boolean; modalType: string }) => void
  trackViewDiscountReminder: () => void
  isFetching: boolean
  pauseReason?: string
}

export const SubPauseOSRView = ({
  promoCode,
  offers,
  onConfirm,
  onKeep,
  title,
  modalType,
  triggered,
  getPauseRecoveryContent,
  modalVisibilityChange,
  trackViewDiscountReminder,
  isFetching,
  pauseReason,
}: Props) => {
  const [shouldDisplayModal, setShouldDisplayModal] = useState(triggered)

  const { closeCurrentModal } = useModal()

  const trackForSubscriptionPause = useCallback(
    (trackingConfig: { settingName: string; action: unknown }, data: Record<string, unknown>) => {
      trackSubscriptionSettingsChange(trackingConfig)(data)
    },
    [],
  )

  const handleAfterModalClose = useCallback(() => {
    modalVisibilityChange({ modalVisibility: false, modalType })
  }, [modalVisibilityChange, modalType])

  const handleModalClose = useCallback(() => {
    trackForSubscriptionPause(
      { settingName: 'recover', action: 'subscription' },
      {
        pause_reason: pauseReason,
        recovery_action: 'close_modal',
      },
    )
    closeCurrentModal()
  }, [closeCurrentModal, trackForSubscriptionPause, pauseReason])

  const handleGoToDeliveriesClick = useCallback(() => {
    trackForSubscriptionPause(
      { settingName: 'recover', action: 'subscription' },
      {
        pause_reason: pauseReason,
        recovery_action: 'Go to manage my deliveries',
      },
    )
  }, [pauseReason, trackForSubscriptionPause])

  const handleKeepClick = useCallback(() => {
    trackForSubscriptionPause(
      { settingName: 'recover', action: 'subscription' },
      {
        pause_reason: pauseReason,
        recovery_action: 'Keep subscription CTA clicked',
        discount_offered: promoCode,
      },
    )
    datadogRum.addAction('keep_subscription_clicked')
    onKeep()
  }, [onKeep, pauseReason, promoCode, trackForSubscriptionPause])

  const handlePauseClick = useCallback(() => {
    trackForSubscriptionPause(
      { settingName: 'pause_subscription', action: 'complete' },
      {
        pause_reason: pauseReason,
        discount_offered: promoCode,
      },
    )
    datadogRum.addAction('cancel_subscription_anyway_clicked')

    onConfirm()
  }, [onConfirm, pauseReason, promoCode, trackForSubscriptionPause])

  useEffect(() => {
    if (triggered) {
      getPauseRecoveryContent()
    }

    setShouldDisplayModal(triggered)
  }, [getPauseRecoveryContent, triggered])

  useEffect(() => {
    if (triggered && offers) {
      trackForSubscriptionPause(
        { settingName: 'view_pause_recovery', action: 'discount' },
        {
          discount_offered: promoCode,
        },
      )
      trackViewDiscountReminder()
    }
  }, [trackViewDiscountReminder, trackForSubscriptionPause, triggered, offers, promoCode])

  return (
    <Modal
      isOpen={shouldDisplayModal}
      name="sub-pause-osr-view"
      onAfterClose={handleAfterModalClose}
    >
      <ModalClose onClose={handleModalClose} />

      <ModalImageHeader>
        <div className={css.backgroundHeader} />
      </ModalImageHeader>

      {isFetching && (
        <ModalBody>
          <Box
            alignItems={AlignItems.Center}
            display={Display.Flex}
            justifyContent={JustifyContent.Center}
            data-testid="loading-osr-modal"
          >
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore TS requires optional props */}
            <LoadingWithPadding />
          </Box>
        </ModalBody>
      )}

      {!isFetching && (
        <>
          <ModalBody>
            <Heading3>{title}</Heading3>
            <Space size={4} direction="vertical" />
            {offers &&
              offers.map((offerMessage, index) => (
                <Box
                  key={offerMessage.replace(' ', '-')}
                  display={Display.Flex}
                  flexDirection={FlexDirection.Column}
                  borderRadius={1}
                >
                  <Box
                    display="flex"
                    alignItems={AlignItems.Center}
                    bg={Color.Success_50}
                    paddingH={6}
                  >
                    <Box paddingV={6}>
                      <Heading4>{offerMessage}</Heading4>
                    </Box>
                  </Box>
                  {index !== offers.length - 1 && (
                    <Box data-testid="offer-plus-icon" alignSelf={AlignSelf.Center}>
                      <Icon name="add" />
                    </Box>
                  )}
                </Box>
              ))}
            <>
              <Space size={5} direction="vertical" />
              <Heading4>Need more flexibility?</Heading4>
              <Space size={2} direction="vertical" />
              <Body2 data-testid="pause-osr-modal-skip-boxes-msg">
                Don&apos;t forget you can always skip boxes at any time on your deliveries page.{' '}
                <Link
                  href={config.client.myDeliveries}
                  onClick={handleGoToDeliveriesClick}
                  size={3}
                >
                  Go to manage my deliveries page
                </Link>
              </Body2>
            </>
          </ModalBody>

          <ModalFooter>
            <ModalButton
              size={ButtonSize.Medium}
              colorVariant={ButtonColorVariant.Secondary}
              onClick={handlePauseClick}
              width="100%"
              style={{
                whiteSpace: 'nowrap',
              }}
            >
              Cancel anyway
            </ModalButton>
            <ModalButton
              size={ButtonSize.Medium}
              colorVariant={ButtonColorVariant.Primary}
              onClick={handleKeepClick}
            >
              Keep subscription
            </ModalButton>
          </ModalFooter>
        </>
      )}
    </Modal>
  )
}
